import {NO_REPOSITORY_GROUP} from '../constants/grouping'

export interface RepoGroupDescriptor {
  /** The "owner/repo" nwo, or the `NO_REPOSITORY_GROUP` sentinel. */
  key: string
  /** Structured repository name from the server, preferred over parsing `key`. */
  sessionSectionHeader?: string
}

// Extracts the repo name from a well-formed "owner/repo" nwo, or `undefined`
// (sentinel, no/multiple slashes, empty segment) so callers fall back to the key.
export function repoShortName(groupKey: string): string | undefined {
  if (groupKey === NO_REPOSITORY_GROUP) return undefined
  const slashIndex = groupKey.indexOf('/')
  if (slashIndex === -1 || slashIndex !== groupKey.lastIndexOf('/')) return undefined
  const owner = groupKey.slice(0, slashIndex)
  const name = groupKey.slice(slashIndex + 1)
  return owner.length > 0 && name.length > 0 ? name : undefined
}

/**
 * Returns the customer-facing owner/repo identifier. Multi-tenant environments
 * append an underscore-delimited tenant suffix to internal owner logins, but
 * public URLs and request parameters must use the unsuffixed display login.
 */
export function repoDisplayNameWithOwner(groupKey: string): string | undefined {
  const repoName = repoShortName(groupKey)
  if (!repoName) return undefined

  const owner = groupKey.slice(0, groupKey.indexOf('/'))
  const displayOwner = owner.split('_', 1)[0]
  return displayOwner ? `${displayOwner}/${repoName}` : undefined
}

function shortNameFor(group: RepoGroupDescriptor): string | undefined {
  if (group.key === NO_REPOSITORY_GROUP) return undefined
  return group.sessionSectionHeader || repoShortName(group.key)
}

/**
 * Labels each repo group by its repository name, falling back to the full
 * `owner/repo` key when the same name appears under more than one owner.
 */
export function computeRepoGroupLabels(groups: readonly RepoGroupDescriptor[]): Map<string, string> {
  const shortNameCounts = new Map<string, number>()
  for (const group of groups) {
    const short = shortNameFor(group)
    if (short == null) continue
    shortNameCounts.set(short, (shortNameCounts.get(short) ?? 0) + 1)
  }

  const labels = new Map<string, string>()
  for (const group of groups) {
    const short = shortNameFor(group)
    if (short == null || (shortNameCounts.get(short) ?? 0) > 1) {
      labels.set(group.key, group.key)
    } else {
      labels.set(group.key, short)
    }
  }
  return labels
}
